import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

type SpanishPlateType = 'moderna' | 'antiga' | 'especial';

const MODERN_LETTERS = 'B-DF-HJ-NP-TV-Z';

/**
 * Codis provincials usats pels sistemes antics.
 * Inclou codis substituits durant la vida dels formats provincials, com IB/PM,
 * GC, GE/GI, OR/OU i codis de tres lletres dels primers anys.
 */
const PROVINCE_CODES = [
  'A',
  'AB',
  'AL',
  'ALB',
  'AV',
  'B',
  'BA',
  'BI',
  'BU',
  'C',
  'CA',
  'CAC',
  'CAS',
  'CC',
  'CE',
  'CO',
  'CR',
  'CS',
  'CU',
  'GC',
  'GE',
  'GI',
  'GR',
  'GU',
  'H',
  'HU',
  'IB',
  'J',
  'L',
  'LE',
  'LO',
  'LU',
  'M',
  'MA',
  'ME',
  'ML',
  'MU',
  'NA',
  'O',
  'OR',
  'OU',
  'P',
  'PA',
  'PM',
  'PO',
  'S',
  'SA',
  'SEG',
  'SE',
  'SG',
  'SO',
  'SS',
  'T',
  'TE',
  'TER',
  'TF',
  'TO',
  'V',
  'VA',
  'VI',
  'Z',
  'ZA',
] as const;

const PROVINCE_CODES_BY_LENGTH = [...PROVINCE_CODES].sort((a, b) => b.length - a.length);

function normalizePlate(raw: string): string {
  return raw.trim().toUpperCase().replace(/[-\s]+/g, '');
}

function isModernPlate(value: string): boolean {
  // 1234 ABC (sense vocals, sense Q ni Ñ)
  const modern = new RegExp(`^[0-9]{4}[${MODERN_LETTERS}]{3}$`);
  return modern.test(value);
}

function isOldPlate(value: string): boolean {
  const prov = PROVINCE_CODES_BY_LENGTH.find((code) => value.startsWith(code));
  if (!prov) return false;

  const rest = value.slice(prov.length);
  return /^[0-9]{1,6}$/.test(rest) || /^[0-9]{4}[A-Z]{1,2}$/.test(rest);
}

function isSpecialPlate(value: string): boolean {
  // Formats especials habituals actuals: històrics, remolcs, ciclomotors,
  // temporals, diplomàtics i forces de l'Estat.
  const specialPatterns: RegExp[] = [
    // Prefix + 4 xifres + 3 lletres modernes: històrics (H), remolcs (R),
    // ciclomotors (C), serveis oficials (S/V) i temporals/provisionals (P, verdes).
    new RegExp(`^[HRCSVP][0-9]{4}[${MODERN_LETTERS}]{3}$`), // H/R/C/S/V/P 1234 BBB
    /^(CD|CC|OI|TA)[0-9]{4,5}$/, // CD/CC/OI/TA 12 123
    /^(PGC|CNP|ET|EA|FN|CGPC)[0-9]{4,6}$/, // Forces de l'Estat
    /^E[0-9]{4,6}$/, // Ertzaintza
  ];
  return specialPatterns.some((rx) => rx.test(value));
}

export function getSpanishPlateType(raw: string | null | undefined): SpanishPlateType | false {
  if (!raw) return false;
  const value = normalizePlate(raw);
  if (!value) return false;
  if (isModernPlate(value)) return 'moderna';
  if (isOldPlate(value)) return 'antiga';
  if (isSpecialPlate(value)) return 'especial';
  return false;
}

/** Comprova si una matrícula és espanyola per format (modern, antic o especials). */
export function isSpanishPlate(raw: string | null | undefined): boolean {
  return getSpanishPlateType(raw) !== false;
}

/** ValidatorFn per a Reactive Forms. Retorna { spanishPlate: true } si NO és vàlida. */
export function spanishPlateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v: string = control.value;
    if (v == null || v === '') return null; // deixa que Validators.required s’encarregui del camp buit
    return isSpanishPlate(v) ? null : { spanishPlate: true };
  };
}
